// Runtime validation at the persistence boundary. TypeScript only checks at
// compile time; data loaded from localStorage (or, later, a server) is unknown
// at runtime, so a saved game from an older schema must be rejected cleanly
// rather than crash the new build.
//
// This is a small hand-written validator to keep the project dependency-free.
// If the validation surface grows (e.g. a backend with many payload shapes),
// swap this module for Zod — the exported `parseGameState` contract stays the
// same so nothing else changes.
import type { GameState } from "../types";

const PHASES = ["menu", "naming", "draft", "season", "result"] as const;

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

function isHistoryEntry(v: unknown): boolean {
  if (!isObj(v)) return false;
  return (
    typeof v["wcc"] === "string" &&
    typeof v["wdc"] === "string" &&
    typeof v["playerWonWcc"] === "boolean" &&
    typeof v["date"] === "number"
  );
}

/**
 * Validate the persisted top-level shape. The season object is large and partly
 * recomputable, so we validate it loosely here and let storage.ts decide whether
 * an in-progress season is usable for the current build.
 */
export function parseGameState(raw: unknown): GameState | null {
  if (!isObj(raw)) return null;
  if (typeof raw["phase"] !== "string" || !(PHASES as readonly string[]).includes(raw["phase"]))
    return null;
  if (!Array.isArray(raw["history"]) || !raw["history"].every(isHistoryEntry)) return null;
  const season = raw["season"];
  if (season !== null && !isObj(season)) return null;
  // unlockedBadges is additive (introduced after v3) — default to [] for any
  // save written before it existed, rather than rejecting the whole state.
  const unlockedBadges = Array.isArray(raw["unlockedBadges"])
    ? raw["unlockedBadges"].filter((b) => typeof b === "string")
    : [];
  // teamName is additive — default to "" for saves written before it existed.
  const teamName = typeof raw["teamName"] === "string" ? raw["teamName"] : "";
  return { ...raw, unlockedBadges, teamName } as unknown as GameState;
}
